import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { selectAccountData } from "../redux/main/accountSlice";

// Componente per visualizzare l'opzione con il cerchio colorato
const ColorDot = ({ color }) => (
    <span
        style={{
            display: 'inline-block',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: color || '#7367f0',
            marginRight: '8px',
            flexShrink: 0
        }}
    />
);

// Componente personalizzato per le opzioni del menu
const CustomOption = ({ innerProps, label, data, isSelected, isFocused }) => (
    <div
        {...innerProps}
        style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: isSelected ? '#3182ce' : isFocused ? '#ebf8ff' : 'white',
            color: isSelected ? 'white' : '#4a5568',
            cursor: 'pointer'
        }}
    >
        <ColorDot color={data.color} />
        <span>{label}</span>
    </div>
);

// Componente personalizzato per i valori selezionati (multi)
const CustomMultiValueLabel = ({ data, innerProps }) => (
    <div {...innerProps} style={{ display: 'flex', alignItems: 'center', padding: '2px 4px' }}>
        <ColorDot color={data.color} />
        <span>{data.label}</span>
    </div>
);

// Componente personalizzato per il valore singolo selezionato
const CustomSingleValue = ({ data, innerProps }) => (
    <div {...innerProps} style={{ display: 'flex', alignItems: 'center' }}>
        <ColorDot color={data.color} />
        <span>{data.label}</span>
    </div>
);

export const EmployeeSelect = ({ selectedOptions, setSelectedOptions, isMulti = true, placeholder = "Seleziona dipendenti...", menuPortalTarget, menuPosition, menuPlacement }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');
    const accountData = useSelector(selectAccountData);
    const accountId = accountData?._id;

    // Funzione per eseguire la chiamata API
    const fetchEmployees = async (searchText) => {
        if (!accountId) return;
        
        setIsLoading(true);
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/users/list?accountId=${accountId}&length=1000`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error(`Errore API: ${response.status}`);
            }
            
            const responseData = await response.json();
            
            // L'API restituisce { data: [...], recordsTotal, recordsFiltered }
            const data = Array.isArray(responseData) ? responseData : (responseData.data || []);
            
            // Filtra i risultati in base al testo di ricerca (se presente)
            let filteredData = data;
            if (searchText && searchText.trim() !== '') {
                const searchLower = searchText.toLowerCase();
                filteredData = data.filter(emp => 
                    (emp.name && emp.name.toLowerCase().includes(searchLower)) || 
                    (emp.surname && emp.surname.toLowerCase().includes(searchLower)) ||
                    (emp.typeLabel && emp.typeLabel.toLowerCase().includes(searchLower))
                );
            }
            
            // Trasforma i dati nel formato richiesto da react-select
            const formattedOptions = filteredData.map(emp => ({
                value: emp._id || emp.id,
                label: `${emp.name || ''} ${emp.surname || ''}`,
                fullLabel: `${emp.name || ''} ${emp.surname || ''} ${emp.typeLabel ? `- ${emp.typeLabel}` : ''}`,
                color: emp.color || '#7367f0',
                data: emp
            }));
            
            setOptions(formattedOptions);
        } catch (error) {
            console.error('Errore nel caricamento dei dipendenti:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce per evitare troppe chiamate API durante la digitazione
    const debouncedFetchEmployees = useCallback(
        debounce(fetchEmployees, 300),
        [accountId, token]
    );

    // Carica le opzioni iniziali
    useEffect(() => {
        fetchEmployees('');
    }, [accountId, token]);

    // Gestisce la modifica dell'input di ricerca
    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        debouncedFetchEmployees(newValue);
    };

    // Stile personalizzato per react-select
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3182ce' : '#cbd5e0'
            },
            borderRadius: '0.375rem',
            backgroundColor: state.isDisabled ? '#f7fafc' : 'white',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#3182ce' 
                : state.isFocused 
                    ? '#ebf8ff' 
                    : base.backgroundColor,
            color: state.isSelected ? 'white' : '#4a5568',
            ':active': {
                backgroundColor: state.isSelected ? '#3182ce' : '#e6f7ff'
            },
            padding: '8px 12px'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#ebf8ff',
            borderRadius: '0.25rem',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#2c5282',
            fontWeight: 500,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#2c5282',
            ':hover': {
                backgroundColor: '#bee3f8',
                color: '#2a4365'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.375rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 10000000,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 10000000,
        }),
        noOptionsMessage: (base) => ({
            ...base,
            fontSize: '0.875rem',
            color: '#718096'
        })
    };

    return (
        <Select
            isMulti={isMulti}
            options={options}
            value={selectedOptions}
            onChange={setSelectedOptions}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            isLoading={isLoading}
            placeholder={placeholder}
            noOptionsMessage={() => "Nessun dipendente trovato"}
            loadingMessage={() => "Caricamento dipendenti..."}
            styles={customStyles}
            isClearable
            closeMenuOnSelect={!isMulti}
            classNamePrefix="employee-select"
            menuPortalTarget={menuPortalTarget !== undefined ? menuPortalTarget : document.body}
            menuPosition={menuPosition || "fixed"}
            menuPlacement={menuPlacement || "auto"}
            components={{
                Option: CustomOption,
                MultiValueLabel: CustomMultiValueLabel,
                SingleValue: CustomSingleValue
            }}
        />
    );
};

export default EmployeeSelect;
